import { useContext, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Autoplay, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { getsimilarproduct } from "../ApiService/similarproduct/similarproduct";
import { FiBookmark, FiEye, FiHeart } from "../assets/icons/vander";
import fallbackImage from "../assets/images/categories/football.jpg";
import { CartContext } from "../components/CartContext";

export default function ArrivalItem({ product }) {
  const [similarProducts, setSimilarProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { addToCart } = useContext(CartContext);
  const BASE_MEDIA_URL = process.env.REACT_APP_MEDIA_BASE_URL;

  useEffect(() => {
    if (!product?.product_id) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getsimilarproduct(product.product_id);
        setSimilarProducts(response?.similar_products || []);
      } catch (err) {
        console.error("Failed to load similar products:", err);
        setError("Failed to load similar products");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [product?.product_id]);

  // Handle product click to navigate with all necessary data
  const handleProductClick = async (productId) => {
    try {
      // Fetch product details using the same API as ShopGridLeftSidebar
      const data = await getsimilarproduct(productId);
      // Navigate to the same route pattern without passing state data
      navigate(`/product-detail-three/products/${productId}`);
    } catch (err) {
      console.error("Failed to fetch product details:", err);
      // Fallback navigation even if API fails (same as ShopGridLeftSidebar)
      navigate(`/product-detail-three/products/${productId}`);
    }
  };

  // Handle add to cart
  const handleAddToCart = (e, item) => {
    e.preventDefault();
    e.stopPropagation();

    // Get the first variant and size if available
    const firstVariant = item.variants?.[0];
    const firstSize = firstVariant?.sizes?.[0];

    const cartItem = {
      id: item.id,
      name: item.name,
      image: item.main_image || firstVariant?.images?.[0],
      amount:
        firstSize?.price?.discounted || firstSize?.price?.mrp || item.desRate,
      quantity: 1,
      color: firstVariant?.color || "Default",
      size: firstSize?.size || "Default",
      brand: item.brand?.name,
    };

    addToCart(cartItem);
    toast.success(`${item.name} added to cart!`);
  };

  // Enhanced skeleton loader component
  const SkeletonCard = () => (
    <div className="animate-pulse">
      <div className="relative overflow-hidden rounded-md bg-gray-200 h-60">
        <div className="absolute -bottom-20 start-3 end-3 h-10 bg-gray-300 rounded-md"></div>
        <div className="absolute top-[10px] end-4 space-y-1">
          <div className="w-10 h-10 bg-gray-300 rounded-full"></div>
          <div className="w-10 h-10 bg-gray-300 rounded-full"></div>
          <div className="w-10 h-10 bg-gray-300 rounded-full"></div>
        </div>
      </div>
      <div className="mt-4">
        <div className="h-4 bg-gray-300 rounded w-3/4 mb-2"></div>
        <div className="h-3 bg-gray-300 rounded w-1/2 mb-2"></div>
        <div className="flex justify-between">
          <div className="h-4 bg-gray-300 rounded w-1/3"></div>
          <div className="h-4 bg-gray-300 rounded w-1/4"></div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="container lg:mt-24 mt-16">
      <div className="grid grid-cols-1 mb-6 text-center">
        <h3 className="font-semibold text-3xl leading-normal">
          Similar Products
        </h3>
      </div>

      {/* Loading State */}
      {loading && (
        <Swiper
          modules={[Autoplay, Pagination]}
          spaceBetween={20}
          slidesPerView={2}
          pagination={{ clickable: true }}
          breakpoints={{
            640: { slidesPerView: 2 },
            768: { slidesPerView: 3 },
            1024: { slidesPerView: 6 },
          }}
        >
          {Array(6)
            .fill(0)
            .map((_, index) => (
              <SwiperSlide key={index}>
                <SkeletonCard />
              </SwiperSlide>
            ))}
        </Swiper>
      )}

      {/* Error State */}
      {!loading && error && (
        <div className="text-center py-8">
          <p className="text-red-500 mb-4">{error}</p>
        </div>
      )}

      {/* No Data State */}
      {!loading && !error && similarProducts.length === 0 && (
        <div className="text-center py-8">
          <p className="text-gray-500">No similar products found.</p>
        </div>
      )}

      {/* Success State */}
      {!loading && !error && similarProducts.length > 0 && (
        <Swiper
          modules={[Autoplay, Pagination]}
          spaceBetween={20}
          slidesPerView={2}
          autoplay={{ delay: 2000 }}
          pagination={{ clickable: true }}
          breakpoints={{
            640: { slidesPerView: 2 },
            768: { slidesPerView: 3 },
            1024: { slidesPerView: 6 },
          }}
        >
          {similarProducts.map((item) => (
            <SwiperSlide key={item.id}>
              <div className="group">
                <div className="relative overflow-hidden shadow rounded-md duration-500 group-hover:shadow-lg">
                  <img
                    src={`${BASE_MEDIA_URL}${
                      item.main_image || item.variants?.[0]?.images?.[0] || ""
                    }`}
                    alt={item.name}
                    className="group-hover:scale-110 duration-500 w-full h-60 object-cover"
                    onError={(e) => {
                      if (e.target.src !== fallbackImage) {
                        // Prevent infinite loop
                        e.target.src = fallbackImage;
                      }
                    }}
                  />

                  {/* Add to Cart Button */}
                  {/* <div className="absolute -bottom-20 group-hover:bottom-3 start-3 end-3 duration-500">
                    <button
                      onClick={(e) => handleAddToCart(e, item)}
                      className="py-2 px-5 inline-block font-semibold text-base text-center bg-slate-900 text-white w-full rounded-md flex items-center justify-center gap-2"
                    >
                      <FiShoppingCart className="size-4" />
                      Add to Cart
                    </button>
                  </div> */}

                  {/* Action Buttons */}
                  <ul className="absolute top-[10px] end-4 opacity-0 group-hover:opacity-100 duration-500 space-y-1 list-none">
                    <li>
                      <button
                        className="size-10 inline-flex items-center justify-center rounded-full bg-white text-slate-900 hover:bg-slate-900 hover:text-white shadow"
                        title="Add to Wishlist"
                      >
                        <FiHeart className="size-4" />
                      </button>
                    </li>
                    <li className="mt-1">
                      <button
                        onClick={() => handleProductClick(item.product_id)}
                        className="size-10 inline-flex items-center justify-center rounded-full bg-white text-slate-900 hover:bg-slate-900 hover:text-white shadow"
                        title="View Details"
                      >
                        <FiEye className="size-4" />
                      </button>
                    </li>
                    <li className="mt-1">
                      <button
                        className="size-10 inline-flex items-center justify-center rounded-full bg-white text-slate-900 hover:bg-slate-900 hover:text-white shadow"
                        title="Add to Bookmarks"
                      >
                        <FiBookmark className="size-4" />
                      </button>
                    </li>
                  </ul>

                  {/* Product Tags */}
                  {item?.tag && (
                    <ul className="absolute top-[10px] start-4 list-none">
                      <li>
                        <span className="bg-red-600 text-white text-[10px] font-bold px-2.5 py-0.5 rounded h-5 inline-block">
                          {item.tag}
                        </span>
                      </li>
                    </ul>
                  )}
                </div>

                {/* Product Details */}
                <div className="mt-4">
                  <button
                    onClick={() => handleProductClick(item.product_id)}
                    className="text-left hover:text-green-500 transition-colors"
                  >
                    <h4 className="font-medium">{item.name}</h4>
                  </button>

                  <p className="text-sm text-gray-500">{item.brand?.name}</p>

                  <div className="flex justify-between items-center mt-1">
                    <div className="flex items-center gap-2">
                      {item.rate && (
                        <del className="text-slate-400 text-sm">
                          {item.rate}
                        </del>
                      )}
                      <span className="font-semibold">{item.desRate}</span>
                    </div>
                    <div className="flex items-center text-amber-400 text-sm">
                      <span>⭐</span>
                      <span>{item.ratings?.avg || 0}</span>
                    </div>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      )}
    </div>
  );
}
